import { useState } from "react";
import dayjs from "dayjs";
import {
  requestService,
  CreatePatientRequestNewRequestDto,
} from "src/api/request/requestService";
import { enqueueSnackbar } from "notistack";

interface UseRequestServiceValidationProps {
  rowID: string;
  selectedServicesData: any[];
  selectedHistoryServicesData: any[]; // To support history view submission
  serviceAddInfo: Record<string, any>;
  currentView: string;
  handleModalClose: () => void;
  requestNote: string;
  procedureDate: Date;
  isPregnant: boolean;
  urgencyLevel: number;
}

export const useRequestServiceValidation = ({
  rowID,
  selectedServicesData,
  selectedHistoryServicesData,
  serviceAddInfo,
  currentView,
  handleModalClose,
  requestNote,
  procedureDate,
  isPregnant,
  urgencyLevel,
}: UseRequestServiceValidationProps) => {
  const [addingToPatient, setAddingToPatient] = useState(false);

  const handleAddToPatient = async () => {
    try {
      setAddingToPatient(true);
      // Combine both regular and history selections, deduplicated by process_id
      // Regular selections take priority (user may have edited quantity)
      const dedupeMap = new Map<number | string, any>();
      selectedServicesData.forEach((s: any) => {
        const pid = s.process_id || s.id;
        if (pid) dedupeMap.set(pid, s);
      });
      selectedHistoryServicesData.forEach((s: any) => {
        const pid = s.process_id || s.originalData?.process_id || s.id;
        if (pid && !dedupeMap.has(pid)) dedupeMap.set(pid, s);
      });
      const allSelectedServicesData = Array.from(dedupeMap.values());

      if (allSelectedServicesData.length === 0) {
        enqueueSnackbar("Lütfen en az bir hizmet seçin", {
          variant: "warning",
          autoHideDuration: 3000,
        });
        setAddingToPatient(false);
        return;
      }

      // Prepare Payload
      const servicesPayload = allSelectedServicesData.map((service: any) => {
        const addInfo = serviceAddInfo[String(service.id)] || undefined;
        const olrIdFromDirection = addInfo?.PROCESS_DIRECTION?.OlrId;

        return {
          ProcessId: service.process_id,
          Quantity: service.quantity || 1,
          ...(service.note && { Note: service.note }), // Include note if present
          ...(olrIdFromDirection !== undefined && { OlrId: olrIdFromDirection }),
          addnewinfo:
            addInfo && Object.keys(addInfo).length > 0 ? addInfo : undefined,
        };
      });

      const requestBody: CreatePatientRequestNewRequestDto = {
        PtId: Number.parseInt(rowID),
        Note: requestNote || null,
        PTime: dayjs(procedureDate || new Date()).format("YYYY-MM-DDTHH:mm:ss"),
        IsPregnant: isPregnant ? "1" : "0",
        Urgent: urgencyLevel > 0 ? String(urgencyLevel) : null,
        Services: servicesPayload,
      };

      const response = await requestService.createPatientRequestNew(
        requestBody
      );

      if (response.issuccess) {
        enqueueSnackbar("Hizmet isteği başarıyla oluşturuldu", {
          variant: "success",
          autoHideDuration: 3000,
        });

        // Close modal after successful creation
        handleModalClose();
      } else {
        enqueueSnackbar(`Hata: ${response.message}`, {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.error("Error creating patient request:", error);
      enqueueSnackbar("Hasta isteği oluşturulurken bir hata oluştu", {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setAddingToPatient(false);
    }
  };

  return {
    addingToPatient,
    setAddingToPatient,
    handleAddToPatient,
  };
};
